import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { api } from '@/lib/apiClient';
import { connectInNewTab } from '@/lib/oauthConnect';
import type { 
  ConnectedPlatformsResponse,
  PlatformConnectResponse,
  WhatsAppConnectRequest,
  WhatsAppConnectResponse,
  PlatformTestResponse,
  PlatformInstructionsResponse,
  FacebookAccount,
  InstagramAccount,
  WhatsAppAccount,
  PlatformAccount
} from '@/types/api';

export const usePlatformIntegrations = () => {
  const queryClient = useQueryClient();

  // Get all connected platforms
  const connectedPlatformsQuery = useQuery({
    queryKey: ['platforms', 'connected'],
    queryFn: () => api.get<ConnectedPlatformsResponse>('/api/platforms/connected'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Connect Facebook Messenger — opens Meta's OAuth in a new tab.
  const connectFacebookMutation = useMutation({
    mutationFn: (redirectUrl: string = '/integrations') =>
      connectInNewTab(() =>
        api.post<PlatformConnectResponse>('/api/platforms/facebook/connect', { redirectUrl })
      ),
    onError: () => {
      toast.error('Failed to connect Facebook Messenger');
    }
  });

  // Connect Instagram Business — opens Meta's OAuth in a new tab.
  const connectInstagramMutation = useMutation({
    mutationFn: (redirectUrl: string = '/integrations') =>
      connectInNewTab(() =>
        api.post<PlatformConnectResponse>('/api/platforms/instagram/connect', { redirectUrl })
      ),
    onError: () => {
      toast.error('Failed to connect Instagram Business');
    }
  });

  // Connect WhatsApp Business
  const connectWhatsAppMutation = useMutation({
    mutationFn: (data: WhatsAppConnectRequest) =>
      api.post<WhatsAppConnectResponse>('/api/platforms/whatsapp/connect', data),
    onSuccess: (data) => {
      // Sending works either way; receiving needs the Business Account subscribed to OUR Meta app.
      // Say so instead of a plain success, or the merchant waits for messages that never arrive.
      // `subscribedToOurApp: null` means the API couldn't check, which is not the same as success.
      if (data?.webhookSubscribed === false || data?.subscribedToOurApp === false) {
        toast(
          "WhatsApp connected for sending, but incoming messages won't reach OmnibizChat: its Business Account isn't subscribed to the OmnibizChat app.",
          { icon: '⚠️', duration: 8000 },
        );
      } else if (data?.subscribedToOurApp === null) {
        toast(
          "WhatsApp connected, but we couldn't verify that incoming messages will reach OmnibizChat. Send the number a test message to confirm.",
          { icon: '⚠️', duration: 8000 },
        );
      } else {
        toast.success('WhatsApp Business connected successfully!');
      }
      queryClient.invalidateQueries({ queryKey: ['platforms', 'connected'] });
      return data;
    },
    onError: () => {
      toast.error('Failed to connect WhatsApp Business');
    }
  });

  // Disconnect platform account
  const disconnectPlatformMutation = useMutation({
    mutationFn: ({ platform, accountId }: { platform: string; accountId: string }) =>
      api.delete(`/api/platforms/${platform}/${accountId}`),
    onSuccess: (_, { platform }) => {
      toast.success(`${platform} account disconnected`);
      queryClient.invalidateQueries({ queryKey: ['platforms', 'connected'] });
    },
    onError: () => {
      toast.error('Failed to disconnect platform account');
    }
  });

  // Test platform connection
  const testConnectionMutation = useMutation({
    mutationFn: ({ platform, accountId, testMessage }: { 
      platform: string; 
      accountId: string;
      testMessage?: string;
    }) =>
      api.post<PlatformTestResponse>(`/api/platforms/${platform}/${accountId}/test`, {
        // Sent through the merchant's real channel, so it carries the product's real name.
        testMessage: testMessage || 'Testing connection from the OmnibizChat dashboard'
      }),
    onSuccess: (data, { platform }) => {
      if (data?.isConnected) {
        toast.success(`${platform} connection test successful!`);
      } else {
        toast.error(`${platform} connection test failed: ${data?.error || 'Unknown error'}`);
      }
    },
    onError: () => {
      toast.error('Connection test failed');
    }
  });

  return {
    // Data
    connectedPlatforms: connectedPlatformsQuery.data,
    isLoading: connectedPlatformsQuery.isLoading,
    error: connectedPlatformsQuery.error,
    
    // Actions
    connectFacebook: connectFacebookMutation.mutate,
    connectInstagram: connectInstagramMutation.mutate,
    connectWhatsApp: connectWhatsAppMutation.mutate,
    disconnectPlatform: disconnectPlatformMutation.mutate,
    testConnection: testConnectionMutation.mutate,
    
    // States
    isConnectingFacebook: connectFacebookMutation.isPending,
    isConnectingInstagram: connectInstagramMutation.isPending,
    isConnectingWhatsApp: connectWhatsAppMutation.isPending,
    isDisconnecting: disconnectPlatformMutation.isPending,
    isTesting: testConnectionMutation.isPending,
    
    // Helper functions
    refetch: () => queryClient.invalidateQueries({ queryKey: ['platforms', 'connected'] }),
  };
};

export const usePlatformInstructions = (platform: string) => {
  return useQuery({
    queryKey: ['platforms', platform, 'instructions'],
    queryFn: () => api.get<PlatformInstructionsResponse>(`/api/platforms/${platform}/instructions`),
    enabled: !!platform,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Individual platform hooks for easier usage
export const useFacebookAccounts = () => {
  const { connectedPlatforms, isLoading } = usePlatformIntegrations();
  return {
    accounts: connectedPlatforms?.accounts.facebook || [],
    count: connectedPlatforms?.summary.facebook || 0,
    isLoading,
  };
};

export const useInstagramAccounts = () => {
  const { connectedPlatforms, isLoading } = usePlatformIntegrations();
  return {
    accounts: connectedPlatforms?.accounts.instagram || [],
    count: connectedPlatforms?.summary.instagram || 0,
    isLoading,
  };
};

export const useWhatsAppAccounts = () => {
  const { connectedPlatforms, isLoading } = usePlatformIntegrations();
  return {
    accounts: connectedPlatforms?.accounts.whatsapp || [],
    count: connectedPlatforms?.summary.whatsapp || 0,
    isLoading,
  };
};